import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { clientActions } from 'core/client/clientReducers'
import Tabs from 'core/elements/tabs'
import Tab from 'core/elements/tabs/Tab'
import useReactRouter from 'use-react-router'
import DocumentMeta from 'core/components/DocumentMeta'
import EntityYamlPage from '../../../common/entity/entity-yaml-page'
import ApiClient from 'api-client/ApiClient'
import jsYaml from 'js-yaml'
import { pathStrOr } from 'utils/fp'
import { IDataVolumeDetailsPageTabs } from '../model'
import { routes } from 'core/utils/routes'
import { isEmpty } from 'ramda'
import useListAction from 'core/hooks/useListAction'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { listDataVolumes, updateDataVolume } from '../new-actions'
import { dataVolumesSelector } from '../selectors'
import Overview from './Overview'
import DeleteDataVolumeDialog from '../DeleteDataVolumeDialog'
import OverviewActions from 'core/elements/overview/OverviewActions'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useParams from 'core/hooks/useParams'

const { qbert } = ApiClient.getInstance()

const actions = {
  rightActions: [
    {
      label: 'Delete',
      icon: 'trash-alt',
      DialogComponent: DeleteDataVolumeDialog,
    },
  ],
}

export default function DataVolumeDetailsPage() {
  const dispatch = useDispatch()
  const { match } = useReactRouter()
  const { id, clusterId } = match.params
  const { globalParams: params } = useGlobalParams(useParams, {})
  const { loading } = useListAction(listDataVolumes, { params })
  const dataVolumes = useSelectorWithParams(dataVolumesSelector, {
    clusterId,
    useGlobalParams: false,
  })
  const dataVolume = useMemo(() => dataVolumes.find((plugin) => plugin.id === id), [
    id,
    dataVolumes,
  ])
  const { update, updating } = useUpdateAction(updateDataVolume)

  useEffect(() => {
    dispatch(
      clientActions.updateBreadcrumbParams({
        clusterId: dataVolume?.clusterName || clusterId,
        id: dataVolume?.name || id,
      }),
    )
    return () => {
      dispatch(clientActions.resetBreadcrumbParams())
    }
  }, [dataVolume?.clusterName, dataVolume?.name, id, clusterId])

  const getDataVolumeYaml = useCallback(async () => {
    if (isEmpty(dataVolume)) return undefined
    return qbert.getDataVolume(dataVolume.clusterId, dataVolume.namespace, dataVolume.name)
  }, [dataVolume])

  const handleDataVolumeUpdate = async (yaml) => {
    const body = jsYaml.load(yaml)
    const namespace = pathStrOr('', 'metadata.namespace', body)
    const name = pathStrOr('', 'metadata.name', body)
    await update({
      id,
      clusterId,
      namespace,
      name,
      body,
    })
  }

  return (
    <>
      <DocumentMeta title="Data Volume" breadcrumbs />
      <div>
        <OverviewActions actions={actions} entity={dataVolume} />
      </div>
      <Tabs route={routes.kubevirtStorage.dataVolumes.details}>
        <Tab label="Overview" value={IDataVolumeDetailsPageTabs.Overview}>
          <Overview dataVolume={dataVolume} loading={loading} />
        </Tab>
        <Tab label="YAML" value={IDataVolumeDetailsPageTabs.Yaml}>
          <EntityYamlPage
            entityType="Data Volume"
            entityName={dataVolume?.name}
            getYamlFn={getDataVolumeYaml}
            handleUpdate={handleDataVolumeUpdate}
            loading={loading || updating}
          />
        </Tab>
      </Tabs>
    </>
  )
}
